import axios from 'axios';

// In production behind a reverse proxy (nginx), same-origin avoids having to bake
// the backend URL at build time.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (data) => api.post('/auth/login', data);
export const developerLogin = (data) => api.post('/auth/developer/login', data);
export const logout = () => api.post('/auth/logout');
export const getMe = () => api.get('/auth/me');

// Developer endpoints
export const getAdmins = () => api.get('/developer/admins');
export const createAdmin = (data) => api.post('/developer/admins', data);
export const getAdmin = (id) => api.get(`/developer/admins/${id}`);
export const updateAdmin = (id, data) => api.patch(`/developer/admins/${id}`, data);
export const deleteAdmin = (id) => api.delete(`/developer/admins/${id}`);
export const getDeveloperStats = () => api.get('/developer/stats');

// Dashboard
export const getDashboardMetrics = () => api.get('/dashboard/metrics');

// Conversations
export const getConversations = (params) => api.get('/conversations', { params });
export const getConversation = (id) => api.get(`/conversations/${id}`);
export const createConversation = (data) => api.post('/conversations', data);
export const updateConversation = (id, data) => api.patch(`/conversations/${id}`, data);
export const acceptConversation = (id) => api.post(`/conversations/${id}/accept`);
export const assignConversation = (id) => api.post(`/conversations/${id}/assign`);
export const unassignConversation = (id) => api.post(`/conversations/${id}/unassign`);
export const transferConversation = (id, attendantId, attendantName) =>
  api.post(`/conversations/${id}/transfer`, { attendant_id: attendantId, attendant_name: attendantName });
export const closeConversation = (id) => api.post(`/conversations/${id}/close`);
export const reopenConversation = (id) => api.post(`/conversations/${id}/reopen`);
export const deleteConversation = (id) => api.delete(`/conversations/${id}`);
export const addNote = (id, note) => api.post(`/conversations/${id}/notes`, { note });
export const markAsRead = (id) => api.post(`/conversations/${id}/read`);

// Messages
export const getMessages = (conversationId) => api.get(`/messages/${conversationId}`);
export const sendMessage = (data) => api.post('/messages', data);

// File Upload
export const uploadFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// Attendants
export const getAttendants = () => api.get('/attendants');
export const createAttendant = (data) => api.post('/attendants', data);
export const updateAttendant = (id, data) => api.patch(`/attendants/${id}`, data);
export const deleteAttendant = (id) => api.delete(`/attendants/${id}`);

// WhatsApp Instances
export const getInstances = () => api.get('/instances');
export const createInstance = (data) => api.post('/instances', data);
export const updateInstance = (id, data) => api.patch(`/instances/${id}`, data);
export const deleteInstance = (id) => api.delete(`/instances/${id}`);

// Developer - Instances
export const getAllInstances = () => api.get('/developer/instances');
export const deleteInstanceAsDeveloper = (id) => api.delete(`/developer/instances/${id}`);

// Developer - Trial
export const activateTrial = (adminId, days) => api.post(`/developer/admins/${adminId}/trial?days=${days}`);
export const deactivateTrial = (adminId) => api.delete(`/developer/admins/${adminId}/trial`);

// Quick Replies
export const getQuickReplies = () => api.get('/quick-replies');
export const createQuickReply = (data) => api.post('/quick-replies', data);
export const deleteQuickReply = (id) => api.delete(`/quick-replies/${id}`);

// Internal Chat
export const getInternalChannels = () => api.get('/internal/channels');
export const getInternalMessages = (channelId, limit = 100) => api.get(`/internal/channels/${channelId}/messages?limit=${limit}`);
export const sendInternalMessage = (data) => api.post('/internal/messages', data);
export const getTeamMembers = () => api.get('/internal/team');

// Cleanup
export const getCleanupStats = () => api.get('/cleanup/stats');
export const runCleanup = () => api.post('/cleanup/old-files');

// Webhook Logs
export const getWebhookLogs = () => api.get('/webhook-logs');

// Integrations
export const getIntegrations = () => api.get('/integrations');
export const createIntegration = (data) => api.post('/integrations', data);
export const updateIntegration = (id, data) => api.patch(`/integrations/${id}`, data);
export const deleteIntegration = (id) => api.delete(`/integrations/${id}`);

// SGP Integration
export const consultarClienteSGP = (cpfCnpj) => api.get(`/sgp/cliente/${encodeURIComponent(cpfCnpj)}`);
export const verificarAcessoSGP = (contrato) => api.get(`/sgp/verificaacesso/${encodeURIComponent(contrato)}`);
export const consultarFinanceiroSGP = (contrato) => api.get(`/sgp/financeiro/${encodeURIComponent(contrato)}`);
export const enviarBoletoSGP = (conversationId, contrato) => api.post('/sgp/enviar-boleto', { conversation_id: conversationId, contrato });
export const enviarPixSGP = (conversationId, contrato) => api.post('/sgp/enviar-pix', { conversation_id: conversationId, contrato });
export const liberarPromessaSGP = (conversationId, contrato, dataPromessa) => api.post('/sgp/liberar-promessa', { conversation_id: conversationId, contrato, data_promessa: dataPromessa });
export const getSGPMotivosOS = () => api.get('/sgp/os/motivos');
export const getSGPTiposOcorrencia = () => api.get('/sgp/os/tipos');
export const abrirOrdemServicoSGP = (data) => api.post('/sgp/os/chamado', data);

// Admin Settings (Business Hours)
export const getAdminSettings = () => api.get('/admin/settings');
export const updateAdminSettings = (data) => api.patch('/admin/settings', data);

// Supervisor endpoints
export const getAttendantMetrics = () => api.get('/supervisor/attendant-metrics');
export const getSupervisorReports = (period = 'week') => api.get(`/supervisor/reports?period=${period}`);

// System Status
export const getSystemStatus = () => api.get('/system/status');
export const checkWebhook = () => api.post('/system/check-webhook');
export const checkInstance = (instanceId) => api.post(`/system/check-instance/${instanceId}`);
export const toggleWebhookTrust = () => api.post('/system/webhook-trust');

// Contacts
export const getContacts = () => api.get('/contacts');
export const createContact = (data) => api.post('/contacts', data);
export const getContact = (id) => api.get(`/contacts/${id}`);
export const updateContact = (id, data) => api.patch(`/contacts/${id}`, data);
export const deleteContact = (id) => api.delete(`/contacts/${id}`);
export const searchContactByPhone = (phone) => api.get(`/contacts/search/${phone}`);

// SGP Extrato de Uso / Tráfego
export const getSGPExtratoUso = (params) => api.get('/sgp/extratouso', { params });

// Seed data
export const seedData = () => api.post('/seed');

export default api;
